import { removeTimeFromStringDate } from '@zspace/dates';
import { formatSalesOrderNumber, sanitizeExternalUrl } from '@zspace/format';
import { ApplicationVariantType, MySoftwareTableRow } from '@zspace/types';
import { ReactNode, useCallback, useMemo } from 'react';
import { Columns, Element, Icon } from 'react-bulma-components';
import { FaArrowUpRightFromSquare, FaCircleInfo } from 'react-icons/fa6';
import { environment } from '../../../environments/environment';
import useIsCustomerUser from '../../shared/hooks/is-customer-user';
import useSalesOrderPermissions from '../../shared/hooks/sales-order-permissions';
import Link from '../../ui/link/link';
import InfiniteTable, {
  CustomInfiniteTableProps,
} from '../../ui/table/infinite-table/infinite-table';
import { Column } from '../../ui/table/types';
import { Tooltip } from '../../ui/tooltip/tooltip';
import styles from './my-software-table.module.scss';

const AVAILABLE_SEATS_TOOLTIP_MESSAGE =
  'All the seats which are not expired and can be assigned to devices.';
const TOTAL_SEATS_TOOLTIP_MESSAGE =
  'The sum of all the assigned, available and expired seats';

export type MySoftwareTableProps =
  CustomInfiniteTableProps<MySoftwareTableRow> & {
    softwareVariantType: ApplicationVariantType;
    includeSalesOrderColumn?: boolean;
  };

export function MySoftwareTable({
  softwareVariantType,
  includeSalesOrderColumn = false,
  ...tableProps
}: MySoftwareTableProps) {
  const isCustomerUser = useIsCustomerUser();
  const {
    userHasSalesOrderManagePermissions,
    userIsJustViewOnlySalesOrderManager,
  } = useSalesOrderPermissions();

  const isDisabledRow = useCallback(
    (row: MySoftwareTableRow) => {
      if (!isCustomerUser) {
        return false;
      }

      const hasManagePermissions = userHasSalesOrderManagePermissions(
        row.salesOrder.id
      );
      const isViewOnlyManager = userIsJustViewOnlySalesOrderManager;

      return !hasManagePermissions && !isViewOnlyManager;
    },
    [
      isCustomerUser,
      userHasSalesOrderManagePermissions,
      userIsJustViewOnlySalesOrderManager,
    ]
  );

  const unmanageableSoftwareWarningText = useMemo(() => {
    const startDate = removeTimeFromStringDate(
      environment.managementHubLicenseStartDate
    );
    return `Software purchased before ${startDate} may not be manageable here.`;
  }, []);

  const ColumnCellContainer = useMemo(
    () =>
      ({
        row,
        children,
      }: {
        row: MySoftwareTableRow;
        children?: ReactNode;
      }) => {
        if (isDisabledRow(row)) {
          return <Element className={styles.disabledCell}>{children}</Element>;
        }
        return children;
      },
    [isDisabledRow]
  );

  const columns: Column<MySoftwareTableRow>[] = useMemo(() => {
    const licensingProductGroupLinkColumn: Column<MySoftwareTableRow> = {
      key: 'licensingProductGroup.name',
      className: includeSalesOrderColumn ? 'is-width-35' : 'is-width-50',
      title: 'Software title',
      render: (el) => (
        <Link to={`/software/${el.licensingProductGroup.id}`}>
          {el.licensingProductGroup.name}
        </Link>
      ),
      sortable: true,
    };

    const licensingProductGroupColumn: Column<MySoftwareTableRow> = {
      key: 'licensingProductGroup.name',
      title: 'Software title',
      className: includeSalesOrderColumn ? 'is-width-30' : 'is-width-45',
      render: (el) => (
        <ColumnCellContainer row={el}>
          {el.licensingProductGroup.name}
        </ColumnCellContainer>
      ),
      sortable: true,
    };

    const endUserColumn: Column<MySoftwareTableRow> = {
      key: 'endUser',
      title: 'End User',
      className: 'is-width-30',
      render: (el) => (
        <ColumnCellContainer row={el}>
          {el.salesOrder.endUser}
        </ColumnCellContainer>
      ),
      sortable: true,
    };

    const softwareTypeColumn: Column<MySoftwareTableRow> = {
      key: 'software.type',
      title: 'Software type',
      className: 'is-width-10',
      render: (el) => (
        <ColumnCellContainer row={el}>Web-based</ColumnCellContainer>
      ),
      sortable: false,
    };

    const knowledgeArticleColumn: Column<MySoftwareTableRow> = {
      key: 'knowledgeArticleUrl',
      title: 'How to access',
      className: 'is-width-15',
      render: (el) => {
        if (!el.knowledgeArticleUrl) {
          return <ColumnCellContainer row={el}>N/A</ColumnCellContainer>;
        }

        return (
          <ColumnCellContainer row={el}>
            <Link to={sanitizeExternalUrl(el.knowledgeArticleUrl)} openNewTab>
              <Icon>
                <FaArrowUpRightFromSquare />
              </Icon>
              <span className="is-underlined">Documentation</span>
            </Link>
          </ColumnCellContainer>
        );
      },
      sortable: false,
    };

    const assignedSeatsColumn: Column<MySoftwareTableRow> = {
      key: 'seats.assigned',
      className: 'is-width-15',
      title: 'Assigned seats',
      render: (el) => (
        <ColumnCellContainer row={el}>{el.seats?.assigned}</ColumnCellContainer>
      ),
      sortable: true,
    };

    const availableSeatsColumn: Column<MySoftwareTableRow> = {
      key: 'seats.available',
      className: 'is-width-20',
      title: (
        <Tooltip
          message={
            <Columns>
              <Columns.Column
                display="flex"
                justifyContent="flex-end"
                className="is-narrow pr-0"
              >
                <Icon color={'primary'}>
                  <FaCircleInfo />
                </Icon>
              </Columns.Column>
              <Columns.Column
                display="flex"
                flexDirection="column"
                className="pl-1"
              >
                <span>{AVAILABLE_SEATS_TOOLTIP_MESSAGE}</span>
                <span>{unmanageableSoftwareWarningText}</span>
              </Columns.Column>
            </Columns>
          }
        >
          <span>Available seats</span>
          <Icon color={'primary'}>
            <FaCircleInfo />
          </Icon>
        </Tooltip>
      ),
      render: (el) => (
        <ColumnCellContainer row={el}>
          {el.seats!.available}
        </ColumnCellContainer>
      ),
      sortable: true,
    };

    const totalSeatsColumn: Column<MySoftwareTableRow> = {
      key: 'seats.total',
      className: 'is-width-15',
      title: (
        <Tooltip
          message={
            <Element display="flex" alignItems="center">
              <Icon color={'primary'} pr={2}>
                <FaCircleInfo />
              </Icon>
              <span>{TOTAL_SEATS_TOOLTIP_MESSAGE}</span>
            </Element>
          }
        >
          <Element display="flex" alignContent="flex-end">
            <span>Total seats</span>
            <Icon color={'primary'}>
              <FaCircleInfo />
            </Icon>
          </Element>
        </Tooltip>
      ),
      render: (el) => (
        <ColumnCellContainer row={el}>{el.seats?.total}</ColumnCellContainer>
      ),
      sortable: true,
    };

    const salesOrderColumn: Column<MySoftwareTableRow> = {
      key: 'salesOrder.number',
      className: 'is-width-15',
      title: 'SO#',
      render: (el) => (
        <Element display="flex" alignItems="center" className="gap-6">
          <Link to={`/sales-orders/${el.salesOrder.id}`}>
            {formatSalesOrderNumber(el.salesOrder.number)}
          </Link>
        </Element>
      ),
      sortable: true,
    };

    const webVariantColumns = [
      licensingProductGroupColumn,
      endUserColumn,
      softwareTypeColumn,
      knowledgeArticleColumn,
    ];

    const nativeVariantColumns = [
      licensingProductGroupLinkColumn,
      assignedSeatsColumn,
      availableSeatsColumn,
      totalSeatsColumn,
    ];

    const baseColumns =
      softwareVariantType === ApplicationVariantType.NATIVE
        ? nativeVariantColumns
        : webVariantColumns;

    if (includeSalesOrderColumn) {
      baseColumns.splice(1, 0, salesOrderColumn);
    }

    return baseColumns;
  }, [
    ColumnCellContainer,
    includeSalesOrderColumn,
    softwareVariantType,
    unmanageableSoftwareWarningText,
  ]);

  return (
    <InfiniteTable
      {...tableProps}
      rowKey="id"
      columns={columns}
      isDisabledSelection={isDisabledRow}
    />
  );
}

export default MySoftwareTable;
